import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { X, Search, Users, AlertTriangle } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'react-hot-toast';

const DriverList = ({ isOpen, onClose, unassignedOnly = false, onSelectDriver }) => {
  const [search, setSearch] = useState('');
  const [developerOnly, setDeveloperOnly] = useState('Exclude');
  const [role, setRole] = useState('');
  const [regionalOfficeFilter, setRegionalOfficeFilter] = useState('');
  const [isApproved, setIsApproved] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [regionalOffices, setRegionalOffices] = useState([]);

  // Fetch regional offices for filter dropdown
  useEffect(() => {
    const fetchRegionalOffices = async () => {
      try {
        const response = await api.get('/regional-offices', { params: { limit: 100 } });
        const offices = response.data?.data?.offices || response.data?.data || [];
        setRegionalOffices(Array.isArray(offices) ? offices : []);
      } catch (error) {
        console.error('Failed to fetch regional offices:', error);
      }
    };
    if (isOpen) {
      fetchRegionalOffices();
    }
  }, [isOpen]);

  // Reset page to 1 when filters or search change
  useEffect(() => {
    setPage(1);
  }, [search, developerOnly, role, regionalOfficeFilter, isApproved]);

  // Fetch drivers using React Query
  const { data: driversResponse, isLoading, isFetching, error, refetch } = useQuery(
    ['drivers-list-modal', { search, developerOnly, role, regionalOfficeFilter, isApproved, page, limit, unassignedOnly }],
    async () => {
      const response = await api.get('/drivers', {
        params: {
          page,
          limit,
          search,
          developerOnly,
          role,
          regionalOfficeFilter,
          isApproved,
          unassignedOnly: unassignedOnly ? 'true' : undefined
        }
      });
      return response;
    },
    {
      enabled: isOpen,
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 0
    }
  );

  if (!isOpen) return null;

  const driversList = driversResponse?.data?.data?.drivers || [];
  const pagination = driversResponse?.data?.data?.pagination || {};

  const handleLogoutDriver = async (driver) => {
    if (!driver.vehicleId?._id) return;

    if (!window.confirm(`Are you sure you want to log out driver "${driver.name}" from vehicle "${driver.vehicleId.regNo}"?`)) {
      return;
    }

    try {
      await api.post(`/vehicles/${driver.vehicleId._id}/logout-driver`);
      toast.success(`Driver "${driver.name}" logged out successfully`);
      refetch();
    } catch (err) {
      console.error('Logout driver error:', err);
      toast.error(err?.response?.data?.message || 'Failed to logout driver');
    }
  };



  const getStatusBadge = (driver) => {
    if (driver.isBlocked) {
      return <span className="badge badge-error">Blocked</span>;
    }
    if (driver.isApproved) {
      return <span className="badge badge-success">Approved</span>;
    }
    return <span className="badge badge-warning">Pending</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-7xl mx-4 max-h-[95vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Users className="h-6 w-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Driver List</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 py-4 border-b border-gray-100">
          {/* Search */}
          <div>
            <label className="form-label">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search name, phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>

          {/* Developer Filter */}
          <div>
            <label className="form-label">Developer Filter</label>
            <select
              value={developerOnly}
              onChange={(e) => setDeveloperOnly(e.target.value)}
              className="input"
            >
              <option value="Exclude">Exclude Developers</option>
              <option value="Include">Include Developers</option>
              <option value="Only">Developers Only</option>
            </select>
          </div>

          {/* Driver Role Filter */}
          <div>
            <label className="form-label">Driver Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input"
            >
              <option value="">All Roles</option>
              <option value="actingdriver">Acting Driver</option>
              <option value="salaried">Salaried</option>
              <option value="dco">DCO</option>
            </select>
          </div>

          {/* Regional Office Filter */}
          <div>
            <label className="form-label">Regional Office</label>
            <select
              value={regionalOfficeFilter}
              onChange={(e) => setRegionalOfficeFilter(e.target.value)}
              className="input"
            >
              <option value="">All Offices</option>
              {regionalOffices.map((office) => (
                <option key={office._id} value={office._id}>
                  {office.name}
                </option>
              ))}
            </select>
          </div>

          {/* Approval Status Filter */}
          <div>
            <label className="form-label">Approval Status</label>
            <select
              value={isApproved}
              onChange={(e) => setIsApproved(e.target.value)}
              className="input"
            >
              <option value="">All Statuses</option>
              <option value="true">Approved</option>
              <option value="false">Pending</option>
            </select>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto min-h-[350px] py-4">
          {error ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Error Loading Drivers</h3>
              <p className="text-gray-600 mb-4">{error?.message || 'Failed to fetch driver list'}</p>
            </div>
          ) : isLoading || isFetching ? (
            <div className="loading-state h-full flex flex-col items-center justify-center py-12">
              <div className="loading-spinner mb-2"></div>
              <p className="text-gray-600">Loading drivers...</p>
            </div>
          ) : driversList.length === 0 ? (
            <div className="empty-state h-full flex flex-col items-center justify-center py-12">
              <Users className="empty-state-icon mb-2 h-10 w-10 text-gray-400" />
              <h3 className="empty-state-title font-medium text-gray-900">No drivers found</h3>
              <p className="empty-state-description text-gray-500 text-sm">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Driver Name</th>
                    <th className="table-header-cell">Phone</th>
                    <th className="table-header-cell">Regional Office</th>
                    <th className="table-header-cell">Vehicle</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {driversList.map((driver) => {
                    const office = regionalOffices.find(o => o._id === driver.regionalOffice);
                    return (
                      <tr key={driver._id} className="table-row">
                        <td className="table-cell">
                          <div className="font-medium text-gray-900">{driver.name}</div>
                        </td>
                        <td className="table-cell text-gray-600">{driver.phone}</td>
                        <td className="table-cell text-gray-600">
                          {office ? office.name : 'N/A'}
                        </td>
                        <td className="table-cell">
                          {driver.vehicleId ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {driver.vehicleId.make} {driver.vehicleId.model}
                              </div>
                              <div className="text-xs text-gray-500">
                                {driver.vehicleId.regNo}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-xs">Not Assigned</span>
                          )}
                        </td>
                        <td className="table-cell">
                          {getStatusBadge(driver)}
                        </td>
                        <td className="table-cell">
                          {unassignedOnly && onSelectDriver ? (
                            <button
                              onClick={() => onSelectDriver(driver)}
                              className="btn btn-success btn-xs text-white transition-colors"
                            >
                              Assign
                            </button>
                          ) : driver.vehicleId ? (
                            <button
                              onClick={() => handleLogoutDriver(driver)}
                              className="btn btn-outline btn-xs text-red-600 border-red-600 hover:bg-red-50 transition-colors"
                            >
                              Logout
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer / Pagination */}
        {pagination && pagination.total > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing page {pagination.current} of {pagination.total} ({pagination.totalRecords} total drivers)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={isFetching || page === 1}
                className="btn btn-outline btn-sm"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.total, p + 1))}
                disabled={isFetching || page === pagination.total}
                className="btn btn-outline btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverList;
